import { useState, useEffect, useRef, useCallback } from 'react'
import type { Product } from '@/data/products'

type ProductViewsModalProps = {
  product: Pick<Product, 'title' | 'views'> | null
  onClose: () => void
}

export function ProductViewsModal({ product, onClose }: ProductViewsModalProps) {
  const [index, setIndex] = useState(0)
  const touchStartX = useRef(0)
  const views = product?.views ?? []

  useEffect(() => {
    setIndex(0)
  }, [product?.title])

  useEffect(() => {
    if (!product) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [product])

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + views.length) % views.length)
  }, [views.length])

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % views.length)
  }, [views.length])

  useEffect(() => {
    if (!product) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [product, onClose, goPrev, goNext])

  if (!product || views.length === 0) return null

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current
    if (Math.abs(delta) < 40) return
    if (delta > 0) goPrev()
    else goNext()
  }

  return (
    <div className="product-views-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="product-views-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${product.title} views`}
      >
        <div className="product-views-modal__header">
          <h3 className="product-views-modal__title">{product.title}</h3>
          <button
            type="button"
            className="product-views-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div
          className="product-views-modal__stage"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button
            type="button"
            className="product-views-modal__nav product-views-modal__nav--prev"
            onClick={goPrev}
            aria-label="Previous view"
          >
            ‹
          </button>

          <div className="product-views-modal__image-wrap">
            <img
              key={views[index]}
              src={views[index]}
              alt={`${product.title} view ${index + 1}`}
              className="product-views-modal__image"
            />
          </div>

          <button
            type="button"
            className="product-views-modal__nav product-views-modal__nav--next"
            onClick={goNext}
            aria-label="Next view"
          >
            ›
          </button>
        </div>

        <div className="product-views-modal__dots" role="tablist" aria-label="View indicators">
          {views.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`View ${i + 1}`}
              className={`product-views-modal__dot${i === index ? ' is-active' : ''}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
